/**
 * What a plan form has to get right before it is allowed to leave the browser.
 *
 * 🔴 THIS EXISTS BECAUSE "CREATE PLAN" FAILED WITH NOTHING TO ACT ON. The form
 * disabled its button on an empty code or name and on nothing else, so an
 * operator who left "Per child" blank — or typed "₹40" or "40.505" into it —
 * sent a body the server refused with a 400, and `client.ts` deliberately never
 * reads a server's message (*"a 400 from `/auth/device` echoes validation
 * messages about fields that carry a child's name"*). What reached the screen
 * was one sentence about a code that was in fact fine. The fields were never
 * wrong on the server; they were unvalidated on the way there.
 *
 * 🔴 IT MIRRORS `createPlanSchema` AND `amountToPaise`, WHICH IS DUPLICATION
 * TAKEN ON PURPOSE. `discountModel.ts` makes the same trade for the same reason:
 * the alternative is a round trip whose answer this product will not read. The
 * duplication is confined to this file, it is pure, and it is tested — and the
 * server remains the only thing that decides. This layer only stops a request
 * that could not possibly succeed.
 *
 * ⚠️ IT CANNOT KNOW A CODE IS TAKEN. Uniqueness lives in the database, and a
 * duplicate is the one plan failure that has to come back as a 400. That is why
 * `describePlanFailure` exists beside this: a rejection after a clean form is
 * almost always the code, and the sentence says so.
 */

/** The wire format for money, mirroring `amountToPaise` exactly. */
const MONEY = /^\d+(?:\.\d{1,2})?$/;
/** Mirrors the `code` field of `createPlanSchema`. */
const CODE = /^[a-z0-9-]+$/;

export const PLAN_CODE_MAX = 40;
export const PLAN_NAME_MAX = 80;
export const PLAN_BLURB_MAX = 200;

export interface PlanDraft {
  code: string;
  name: string;
  perStudent: string;
  floor: string;
  blurb: string;
}

/** One message per field, `null` when the field is fine. Empty is not an error
 *  until the operator has typed something or pressed the button — the screen
 *  decides when to show these, this decides what they say. */
export interface PlanDraftErrors {
  code: string | null;
  name: string | null;
  perStudent: string | null;
  floor: string | null;
  blurb: string | null;
}

const money = (raw: string, field: string): string | null => {
  const value = raw.trim();
  if (value === "") return `${field} is needed — type the amount in rupees.`;
  if (!MONEY.test(value))
    return `${field} is digits with at most two decimals, like 40.50. No ₹ sign, no commas.`;
  return null;
};

export function validatePlanDraft(draft: PlanDraft): PlanDraftErrors {
  const code = draft.code.trim();
  const name = draft.name.trim();
  const blurb = draft.blurb.trim();

  return {
    code:
      code === ""
        ? "A plan needs a code — it is what invoices and the audit trail refer to."
        : !CODE.test(code)
          ? "Lowercase letters, digits and hyphens only, like school-annual."
          : code.length > PLAN_CODE_MAX
            ? `A code is at most ${String(PLAN_CODE_MAX)} characters.`
            : null,
    name:
      name === ""
        ? "A plan needs a name — this is what a school sees."
        : name.length > PLAN_NAME_MAX
          ? `A name is at most ${String(PLAN_NAME_MAX)} characters.`
          : null,
    perStudent: money(draft.perStudent, "The per-child rate"),
    floor: money(draft.floor, "The least a school pays"),
    blurb:
      blurb.length > PLAN_BLURB_MAX
        ? `A description is at most ${String(PLAN_BLURB_MAX)} characters.`
        : null,
  };
}

export const planDraftIsValid = (draft: PlanDraft): boolean =>
  Object.values(validatePlanDraft(draft)).every((message) => message === null);

/**
 * 🔴 THE FLOOR BELOW THE RATE IS LEGAL AND IS STILL WORTH SAYING. A plan whose
 * minimum is lower than one child's rate has no minimum at all — every school
 * pays seats × rate. That is a valid plan and the server takes it, so this is a
 * remark and never an error: it returns a sentence for the screen to show in the
 * quiet tone, and blocks nothing.
 */
export function planFloorRemark(draft: PlanDraft): string | null {
  if (!MONEY.test(draft.perStudent.trim()) || !MONEY.test(draft.floor.trim())) return null;
  const rate = Number(draft.perStudent.trim());
  const floor = Number(draft.floor.trim());
  if (floor === 0) return "With no minimum, a school of one child pays for one child.";
  if (floor <= rate)
    return "This minimum is at or below one child's rate, so it will never apply.";
  return `The minimum covers the first ${String(Math.ceil(floor / rate))} children.`;
}

/**
 * What to say when the server refuses, from the status alone.
 *
 * ⚠️ THE STATUS IS ALL THERE IS. `client.ts` reads a `code` field only when it
 * matches SCREAMING_SNAKE, and `BillingService.createPlan` throws a plain
 * `BadRequestException` for a duplicate code — so a 400 arriving after a form
 * this file has already approved is, in practice, the one thing this file
 * cannot check.
 */
export function describePlanFailure(status: number): string {
  if (status === 0) return "That did not reach the server. Check the connection and try again.";
  if (status === 400)
    return "The server refused this plan. A plan with that code most likely already exists — try another code.";
  if (status === 401 || status === 403)
    return "This sign-in is not allowed to create plans. Sign in as a platform operator.";
  if (status === 409) return "A plan with that code already exists.";
  if (status >= 500) return "The server had a problem. Nothing was created — try again.";
  return "That plan was not created.";
}
